
import { AIModule } from '../../types';

export const AUTONOMOUS_SYSTEMS_MODULE_1: AIModule = {
  id: 'autonomous-systems-foundations',
  title: 'Autonomous Systems Foundations',
  description: 'Introduction to autonomous systems, robotics, and intelligent control.',
  duration: '4 hours',
  difficulty: 'medium',
  content: `
# Autonomous Systems Foundations

Autonomous systems are engineered systems that are capable of accomplishing goals while operating independently of external control during periods of their operation. These systems combine artificial intelligence, robotics, and control theory to create intelligent machines.

## What are Autonomous Systems?

Autonomous systems are self-governing systems that can perform tasks and make decisions without direct human intervention. They use sensors to perceive their environment, process this information, and take appropriate actions to achieve their objectives.

## Key Components of Autonomous Systems

### 1. Perception
The ability to sense and understand the environment through various sensors.

**Sensors Include:**
- Cameras (computer vision)
- LiDAR (Light Detection and Ranging)
- Radar
- Ultrasonic sensors
- IMU (Inertial Measurement Unit)
- GPS

### 2. Planning and Decision Making
The cognitive ability to plan actions and make decisions based on current state and objectives.

**Techniques:**
- Path planning algorithms
- Motion planning
- Task planning
- Decision trees and state machines

### 3. Control and Actuation
The ability to execute planned actions through actuators and control systems.

**Components:**
- Motors and actuators
- Control loops (PID controllers)
- Real-time control systems

## Levels of Autonomy

Based on SAE International standards for autonomous vehicles:

1. **Level 0**: No automation - Human performs all tasks
2. **Level 1**: Driver assistance - System assists with steering OR acceleration/deceleration
3. **Level 2**: Partial automation - System controls steering AND acceleration/deceleration
4. **Level 3**: Conditional automation - System performs all tasks but human must be ready to take over
5. **Level 4**: High automation - System performs all tasks in specific conditions
6. **Level 5**: Full automation - System performs all tasks in all conditions

## Simple Autonomous Navigation Example

\`\`\`python
import numpy as np
import matplotlib.pyplot as plt

class SimpleRobot:
    def __init__(self, x=0, y=0, heading=0):
        self.x = x
        self.y = y
        self.heading = heading  # in radians
        self.speed = 1.0
        
    def move_forward(self, distance):
        """Move robot forward by given distance"""
        self.x += distance * np.cos(self.heading)
        self.y += distance * np.sin(self.heading)
    
    def turn(self, angle):
        """Turn robot by given angle (in radians)"""
        self.heading += angle
        self.heading = self.heading % (2 * np.pi)  # Keep angle in [0, 2π]
    
    def distance_to_goal(self, goal_x, goal_y):
        """Calculate distance to goal"""
        return np.sqrt((goal_x - self.x)**2 + (goal_y - self.y)**2)
    
    def angle_to_goal(self, goal_x, goal_y):
        """Calculate angle to goal"""
        return np.arctan2(goal_y - self.y, goal_x - self.x)
    
    def navigate_to_goal(self, goal_x, goal_y, tolerance=0.1):
        """Simple navigation algorithm"""
        while self.distance_to_goal(goal_x, goal_y) > tolerance:
            # Calculate desired heading
            desired_heading = self.angle_to_goal(goal_x, goal_y)
            
            # Calculate angle difference
            angle_diff = desired_heading - self.heading
            
            # Normalize angle difference to [-π, π]
            angle_diff = (angle_diff + np.pi) % (2 * np.pi) - np.pi
            
            # Turn towards goal
            if abs(angle_diff) > 0.1:  # If not facing goal
                turn_amount = np.sign(angle_diff) * min(abs(angle_diff), 0.1)
                self.turn(turn_amount)
            else:
                # Move forward if facing goal
                step_size = min(0.1, self.distance_to_goal(goal_x, goal_y))
                self.move_forward(step_size)
            
            print(f"Robot position: ({self.x:.2f}, {self.y:.2f}), heading: {self.heading:.2f}")

# Example usage
robot = SimpleRobot(0, 0, 0)
print("Navigating to goal (5, 3)")
robot.navigate_to_goal(5, 3)
print(f"Final position: ({robot.x:.2f}, {robot.y:.2f})")
\`\`\`

Autonomous systems represent the convergence of multiple technologies and continue to advance rapidly with improvements in AI, sensor technology, and computing power.
  `,
  quiz: {
    id: 'autonomous-systems-foundations-quiz',
    title: 'Autonomous Systems Foundations Quiz',
    passThreshold: 75,
    questions: [
      {
        id: 'q1',
        question: 'What are the three key components of autonomous systems?',
        type: 'multiple-choice',
        options: [
          'Hardware, Software, Network',
          'Perception, Planning, Control',
          'Sensors, Processors, Actuators',
          'Input, Processing, Output'
        ],
        correctAnswer: 1,
        explanation: 'The three key components are Perception (sensing the environment), Planning (decision making), and Control (executing actions).',
        difficulty: 'medium',
        points: 15
      }
    ]
  },
  resources: [
    {
      id: 'autonomous-systems-video',
      title: 'Autonomous Systems Overview',
      type: 'video',
      url: 'https://example.com/autonomous-systems',
      description: 'Introduction to autonomous systems and robotics'
    }
  ]
};
